import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { CallGroupTypes } from '@frontend/api-call-group';
import { CallGroupsApi } from '@frontend/api-call-groups';
import { DevicesQueries } from '@frontend/api-devices';
import { ForwardingNumberApi, ForwardingNumberTypes } from '@frontend/api-forwarding-number';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useInvalidateQueries } from '@frontend/location-helpers';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useScopedQuery } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  ContentLoader,
  EditSimpleIcon,
  Table,
  useModalControl,
  useAlert,
  Heading,
  PrimaryButton,
  Text,
  emptyStateGraphics,
  ModalControlModalProps,
  IconProps,
  TextLink,
  Chip,
  Truncated,
} from '@frontend/design-system';
import { DEFAULT_PAGE_SIZE } from '../../constants';
import { useLocationResolver } from '../../hooks/useLocationResolver';
import { queryKeys } from '../../query-keys';
import { trackingId } from '../../tracking';
import { usePhoneSettingsMutation } from '../../utils/use-phone-settings-query';
import { getDeviceKey } from './device-key';
import { EditDeviceModal } from './edit-device-modal';
import { RingSlider } from './ring-slider';

interface Props {
  onAssignDevice: () => void;
  callGroup: CallGroupTypes.CallGroupTypes;
  devices?: CallGroupTypes.SipProfileType[];
  enableDeviceEditing?: boolean;
  tableLayout?: 'combined' | 'separated';
  removeIcon?: FC<PropsWithChildren<IconProps>>;
  showLocationColumn?: boolean;
  onRemoveDevice: (deviceKey: string) => void;
  onRingChange: (deviceKey: string, delayStart: number, timeout: number) => void;
}

export const CallLegs = ({
  onAssignDevice,
  callGroup,
  devices,
  enableDeviceEditing = false,
  tableLayout = 'combined',
  removeIcon: RemoveIcon = () => <Icon name='remove2' color='subdued' />,
  showLocationColumn = false,
  onRemoveDevice,
  onRingChange,
}: Props) => {
  const { t } = useTranslation('phone', { keyPrefix: 'call-groups' });
  const invalidateQueries = useInvalidateQueries();
  const alert = useAlert();
  const editDeviceModalController = useModalControl();
  const { navigate } = useSettingsNavigate();

  const deskPhonesAndSoftPhones = callGroup.devices.filter((device) => device.deskPhone || device.softPhone);
  const mobilePhones = callGroup.devices.filter((device) => device.mobilePhone);
  // replace mobilePhones name with user friendly name from devices data
  mobilePhones.forEach((mobile) => {
    const match = devices?.find((data) => data.device?.deviceId === mobile.mobilePhone?.deviceId);
    if (match && mobile.mobilePhone) {
      mobile.mobilePhone.name = match.device?.deviceName;
      mobile.mobilePhone.sipName = match.name;
      mobile.mobilePhone.createdAt = match.device?.createdAt;
    }
  });
  const forwardingNumbers = callGroup.devices.filter((device) => device.forwardingNumber);

  const hasData = deskPhonesAndSoftPhones.length > 0 || mobilePhones.length > 0 || forwardingNumbers.length > 0;
  const EmptyState = emptyStateGraphics.sync_your_phone;
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');

  const { data: { devices: devicesList } = { devices: [] } } = DevicesQueries.useGetDevicesList({
    tenantId: settingsTenantLocation?.phoneTenantId,
  });

  const isUnifyTenant = settingsTenantLocation?.locationType === 'unify';

  // Determine if location column should be displayed (either forced by prop or auto-determined by tenant type)
  const displayLocationColumn = showLocationColumn || (tableLayout === 'combined' && isUnifyTenant);

  // Query forwarding numbers to get location information
  const { data: allForwardingNumbers = [] } = useScopedQuery({
    queryKey: queryKeys.forwardingNumbers(),
    queryFn: () => ForwardingNumberApi.list({ locationId: settingsTenantLocation?.id }),
    enabled: !!settingsTenantLocation?.id && isUnifyTenant,
    meta: {
      error: {
        message: 'CallLegs:forwardingNumbersQuery | Failed to fetch forwarding numbers',
        data: {
          callGroupId: callGroup.callGroupId,
          phoneTenantId: settingsTenantLocation?.phoneTenantId,
          locationId: settingsTenantLocation?.id,
        },
      },
    },
  });

  // Use location resolver for all location-related logic
  const locationResolver = useLocationResolver({
    allForwardingNumbers,
    allDevices: devicesList,
    tenantId: settingsTenantLocation?.phoneTenantId,
    enabled: isUnifyTenant,
  });

  const removeCallLeg = usePhoneSettingsMutation({
    mutationFn: (id: string) => CallGroupsApi.deleteCallLeg(id),
    onSuccess: () => {
      alert.success(t('Device removed successfully.'));
      invalidateQueries(queryKeys.callGroup(callGroup.callGroupId));
      invalidateQueries(queryKeys.listCallGroups(settingsTenantLocation?.phoneTenantId ?? ''));

      invalidateQueries(queryKeys.callGroups());
    },
    onError: () => {
      alert.error(t('Unable to remove the device.'));
    },
  });

  const removeAction = [
    {
      Icon: RemoveIcon,
      label: t('Remove Device'),
      onClick: (device: CallGroupTypes.Device) => onRemoveDevice(getDeviceKey(device)),
      trackingId: trackingId({ context: 'setting', feature: 'call-groups', details: 'remove-device' }),
    },
  ];

  const deviceActions = [
    ...(enableDeviceEditing
      ? [
          {
            Icon: EditSimpleIcon,
            label: t('Edit Device'),
            onClick: (device: CallGroupTypes.Device) =>
              navigate({
                to: '/phone/devices/:id',
                params: { id: device.deskPhone?.deviceId ?? device.softPhone?.deviceId ?? '' },
              }),
            trackingId: trackingId({ context: 'setting', feature: 'call-groups', details: 'edit-device' }),
          },
        ]
      : []),
    ...removeAction,
  ];

  return (
    <>
      {!hasData && (
        <section css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <EmptyState
            css={css`
              height: 200px;
            `}
          />
          <Text>{t('No items to display. Assigned devices will display here.')}</Text>
          <PrimaryButton
            size='large'
            css={{ width: '150px', margin: theme.spacing(4, 0) }}
            onClick={onAssignDevice}
            trackingId={trackingId({ context: 'setting', feature: 'call-groups', details: 'open-assign-device' })}
          >
            {t('Assign Device')}
          </PrimaryButton>
        </section>
      )}
      {/* Single table view: displays all device types in one table */}
      {hasData && tableLayout === 'combined' && (
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          {tableLayout === 'combined' && (
            <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text size='medium' weight='bold'>
                {t('Assigned Devices')}
              </Text>
              <TextLink
                css={{ fontWeight: 'bold' }}
                onClick={() => {
                  onAssignDevice();
                }}
              >
                {t('Assign Devices')}
              </TextLink>
            </div>
          )}

          <DeviceTable
            devices={[...deskPhonesAndSoftPhones, ...mobilePhones, ...forwardingNumbers]}
            labels={{
              heading: '',
              nameHeader: t('Assigned Device (Type)'),
              uniqueIdHeader: displayLocationColumn ? t('Location') : t('MAC Address / SIP Name / Phone Number'),
            }}
            actions={deviceActions}
            isRemoving={removeCallLeg.isLoading}
            ringtone={callGroup.ringtone}
            modalProps={editDeviceModalController.modalProps}
            displayLocationColumn={displayLocationColumn}
            enableDeviceEditing={enableDeviceEditing}
            callGroup={callGroup}
            allDevices={devicesList}
            showDeviceTypes={true}
            allForwardingNumbers={allForwardingNumbers}
            locationResolver={locationResolver}
            onRingChange={onRingChange}
          />
        </div>
      )}
      {/* Separate table view: displays each device type in its own table */}
      {hasData && tableLayout === 'separated' && (
        <>
          {!!deskPhonesAndSoftPhones.length && (
            <DeviceTable
              devices={deskPhonesAndSoftPhones}
              labels={{
                heading: t('Devices'),
                nameHeader: t('Assigned Device'),
                uniqueIdHeader: displayLocationColumn ? t('Location') : t('MAC Address'),
              }}
              actions={deviceActions}
              isRemoving={removeCallLeg.isLoading}
              ringtone={callGroup.ringtone}
              modalProps={editDeviceModalController.modalProps}
              displayLocationColumn={displayLocationColumn}
              enableDeviceEditing={enableDeviceEditing}
              callGroup={callGroup}
              allDevices={devicesList}
              showDeviceTypes={false}
              allForwardingNumbers={allForwardingNumbers}
              locationResolver={locationResolver}
              onRingChange={onRingChange}
            />
          )}
          {!!mobilePhones.length && (
            <DeviceTable
              devices={mobilePhones}
              labels={{
                heading: t('Mobile App'),
                nameHeader: t('Device Name'),
                uniqueIdHeader: displayLocationColumn ? t('Location') : t('SIP Name'),
              }}
              actions={removeAction}
              isRemoving={removeCallLeg.isLoading}
              ringtone={callGroup.ringtone}
              modalProps={editDeviceModalController.modalProps}
              displayLocationColumn={displayLocationColumn}
              enableDeviceEditing={enableDeviceEditing}
              callGroup={callGroup}
              allDevices={devicesList}
              showDeviceTypes={false}
              allForwardingNumbers={allForwardingNumbers}
              locationResolver={locationResolver}
              onRingChange={onRingChange}
            />
          )}
          {!!forwardingNumbers.length && (
            <DeviceTable
              devices={forwardingNumbers}
              labels={{
                heading: t('Forwarding Numbers'),
                nameHeader: t('Name'),
                uniqueIdHeader: displayLocationColumn ? t('Location') : t('Phone Number'),
              }}
              actions={removeAction}
              isRemoving={removeCallLeg.isLoading}
              ringtone={callGroup.ringtone}
              modalProps={editDeviceModalController.modalProps}
              displayLocationColumn={displayLocationColumn}
              enableDeviceEditing={enableDeviceEditing}
              callGroup={callGroup}
              allDevices={devicesList}
              showDeviceTypes={false}
              allForwardingNumbers={allForwardingNumbers}
              locationResolver={locationResolver}
              onRingChange={onRingChange}
            />
          )}
        </>
      )}
    </>
  );
};

interface DeviceTableProps {
  devices: CallGroupTypes.Device[];
  labels: {
    heading: string;
    nameHeader: string;
    uniqueIdHeader: string;
  };
  actions: {
    Icon: FC<PropsWithChildren<IconProps>>;
    label: string;
    onClick: (device: CallGroupTypes.Device) => void;
  }[];
  isRemoving?: boolean;
  ringtone: string;
  modalProps: ModalControlModalProps;
  displayLocationColumn?: boolean;
  enableDeviceEditing?: boolean;
  callGroup?: CallGroupTypes.CallGroupTypes;
  allDevices?: any[];
  showDeviceTypes?: boolean;
  allForwardingNumbers?: ForwardingNumberTypes.FwdNumberModel[];
  locationResolver?: {
    getDeviceLocation: (deviceId: string) => string;
    getForwardingNumberLocation: (forwardingNumberId: string) => string;
    isLoading: boolean;
  };
  onRingChange: (deviceKey: string, delayStart: number, timeout: number) => void;
}

export const DeviceTable: React.FC<DeviceTableProps> = ({
  devices,
  labels,
  actions,
  isRemoving,
  modalProps,
  displayLocationColumn = false,
  enableDeviceEditing = false,
  showDeviceTypes = false,
  locationResolver,
  onRingChange,
}) => {
  const { t } = useTranslation('phone', { keyPrefix: 'call-groups' });
  // State to manage pagination config
  const [pageConfig, setPageConfig] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });

  const sortedDevices = useMemo(() => {
    return [...devices].sort((a, b) => {
      const nameA = a.deskPhone?.name || a.softPhone?.name || a.mobilePhone?.name || a.forwardingNumber?.name || '';
      const nameB = b.deskPhone?.name || b.softPhone?.name || b.mobilePhone?.name || b.forwardingNumber?.name || '';
      return nameA.localeCompare(nameB);
    });
  }, [devices]);

  const paginatedData = useMemo(() => {
    const result = sortedDevices.slice(
      (pageConfig.pageNumber - 1) * pageConfig.pageSize,
      pageConfig.pageNumber * pageConfig.pageSize
    );
    return result;
  }, [sortedDevices, pageConfig.pageNumber, pageConfig.pageSize]);

  // Automatically adjust page number if the current page is empty
  useEffect(() => {
    const totalItems = devices.length ?? 0;
    const maxPage = Math.ceil(totalItems / pageConfig.pageSize);
    if (pageConfig.pageNumber > maxPage) {
      setPageConfig((prev) => ({ ...prev, pageNumber: maxPage }));
    }
  }, [devices, pageConfig]);

  const getDeviceNameWithType = (device: CallGroupTypes.Device) => {
    const name =
      device.deskPhone?.name ||
      device.softPhone?.name ||
      device.mobilePhone?.name ||
      device.forwardingNumber?.name ||
      '';

    if (!showDeviceTypes) {
      return name;
    }

    let type = '';
    if (device.deskPhone) {
      type = t('desk');
    } else if (device.softPhone) {
      type = t('softphone');
    } else if (device.mobilePhone) {
      type = t('mobile');
    } else if (device.forwardingNumber) {
      type = t('forwarding number');
    }

    return `${name} (${type})`;
  };

  const getTooltipContent = (device: CallGroupTypes.Device) => {
    const name = getDeviceNameWithType(device);

    if (device.mobilePhone) {
      let additionalInfo = '';
      if (device.mobilePhone.sipName) {
        additionalInfo += device.mobilePhone.sipName;
      }
      if (device.mobilePhone.createdAt) {
        const dateObj = new Date(device.mobilePhone.createdAt);
        const formattedDate = dateObj.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
        additionalInfo += ` Created: ${formattedDate}`;
      }
      return additionalInfo ? `${name} ${additionalInfo}` : name;
    }

    return name;
  };

  return (
    <>
      {labels.heading && (
        <Heading css={{ marginTop: theme.spacing(1) }} level={3}>
          {labels.heading}
        </Heading>
      )}
      <Table
        colConfig={[
          {
            id: 'name',
            Header: labels.nameHeader,
            accessor: getDeviceNameWithType,
            width: 210,
            cellRenderer: (name: string, rowData?: CallGroupTypes.Device) => {
              if (!rowData) return name;
              return (
                <Truncated
                  TooltipComponent={
                    <Text size='medium' color='white'>
                      {getTooltipContent(rowData)}
                    </Text>
                  }
                >
                  {name}
                </Truncated>
              );
            },
          },
          ...(enableDeviceEditing || displayLocationColumn
            ? [
                {
                  id: 'uniqueId',
                  Header: labels.uniqueIdHeader,
                  accessor: (device: CallGroupTypes.Device) => {
                    if (displayLocationColumn && locationResolver) {
                      if (device.forwardingNumber) {
                        const locationName = locationResolver.getForwardingNumberLocation(
                          device.forwardingNumber.forwardingNumberId
                        );
                        return { label: locationName || '', type: 'single' };
                      }

                      const deviceId =
                        device.deskPhone?.deviceId || device.softPhone?.deviceId || device.mobilePhone?.deviceId;

                      if (!deviceId) return { label: '', type: 'single' };

                      const locationName = locationResolver.getDeviceLocation(deviceId);
                      return { label: locationName || '', type: 'single' };
                    }
                    return (
                      device.deskPhone?.macAddress ||
                      device.mobilePhone?.sipName ||
                      device.forwardingNumber?.number ||
                      ''
                    );
                  },
                  cellRenderer: displayLocationColumn
                    ? (data: { label: string; type: 'multi' | 'single' }) =>
                        !data.label ? (
                          '--'
                        ) : data.type === 'multi' ? (
                          <Chip.MultiChip>{data.label}</Chip.MultiChip>
                        ) : (
                          <Chip.SingleChip>{data.label}</Chip.SingleChip>
                        )
                    : undefined,
                },
              ]
            : []),
          {
            Header: t('Ring Start and End Times (6 seconds usually equals 1 ring)'),
            accessor: (device) => ({
              device,
              isMobilePhone: !!device.mobilePhone,
              devices: devices,
              deviceKey: getDeviceKey(device),
              onRingChange,
            }),
            cellRenderer: RingSlider,
            sortType: 'basic',
            id: 'ringTime',
          },
        ]}
        data={paginatedData}
        hasResponsiveColWidths
        isPaginated={devices.length > 10}
        rowActions={{ actions }}
        manualPaginationConfig={{
          page: pageConfig.pageNumber,
          rowsPerPageOptions: [10, 20],
          handleChange: (action: 'next' | 'prev') => {
            if (action === 'next') {
              setPageConfig({ ...pageConfig, pageNumber: pageConfig.pageNumber + 1 });
            } else {
              setPageConfig({ ...pageConfig, pageNumber: pageConfig.pageNumber - 1 });
            }
          },
          onNumRowsChange: (num) => {
            setPageConfig({ pageNumber: 1, pageSize: num });
          },
          hasNext: pageConfig.pageNumber * pageConfig.pageSize < (devices.length || 0),
          hasPrevious: pageConfig.pageNumber > 1,
        }}
      />
      <EditDeviceModal {...modalProps} />
      <ContentLoader show={isRemoving} />
    </>
  );
};
